import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import appCss from "../styles.css?url";
import { SITE_LAST_UPDATED } from "@/lib/siteMeta";
import { lazy, Suspense } from "react";
// Sonner is only needed when a toast fires — load it off the critical path.
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { CookieBanner } from "@/components/CookieBanner";
import { PersonaFiPopup } from "@/components/PersonaFiPopup";
import { ConsentScripts } from "@/components/ConsentScripts";
import { ScrollToTop } from "@/components/ScrollToTop";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { initWebVitals } from "@/lib/webVitals";

// Consent-gated LinkedIn SDK loader — ported from index.html. Loads only after
// the visitor grants marketing consent, and only when the browser is idle so it
// never competes with the LCP paint.
const LINKEDIN_LOADER = `(function () {
  var loaded = false;
  var load = function () {
    if (loaded) return;
    loaded = true;
    var s = document.createElement('script');
    s.src = 'https://platform.linkedin.com/in.js';
    s.type = 'text/javascript';
    s.async = true;
    s.text = 'lang: en_US';
    document.body.appendChild(s);
  };
  var allowed = function () {
    try {
      var raw = window.localStorage.getItem('agape.cookieConsent.v1');
      return !!raw && !!JSON.parse(raw).consent.marketing;
    } catch (e) { return false; }
  };
  var maybeLoad = function () {
    if (!allowed()) return;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(load, { timeout: 4000 });
    } else {
      setTimeout(load, 2000);
    }
  };
  window.addEventListener('load', maybeLoad);
  window.addEventListener('cookieconsentchange', maybeLoad);
})();`;

// Pre-hydration fallback for the cookie banner. The banner is server-rendered,
// so on slow mobile devices it is visible (and tappable) before React attaches
// event handlers. This native delegated listener makes Accept/Reject work
// immediately; once React hydrates it sets window.__cookieConsentHydrated and
// takes over.
const COOKIE_PREHYDRATION_SCRIPT = `(function () {
  document.addEventListener('click', function (e) {
    if (window.__cookieConsentHydrated) return;
    var t = e.target && e.target.closest ? e.target.closest('[data-cookie-action]') : null;
    if (!t) return;
    var action = t.getAttribute('data-cookie-action');
    var consent = {
      necessary: true,
      analytics: action === 'accept',
      marketing: action === 'accept',
    };
    try {
      window.localStorage.setItem('agape.cookieConsent.v1', JSON.stringify({
        version: 1,
        decidedAt: new Date().toISOString(),
        consent: consent,
      }));
    } catch (err) { /* ignore */ }
    var banner = document.querySelector('[data-cookie-banner]');
    if (banner) banner.style.display = 'none';
    window.dispatchEvent(new CustomEvent('cookieconsentchange', { detail: consent }));
  }, true);
})();`;

const ORGANIZATION_JSONLD = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://agapeco.net/#organization",
    name: "AGAPE Automation Systems Sp. z o.o.",
    alternateName: "AGAPE",
    url: "https://agapeco.net/",
    logo: "https://agapeco.net/agape-logo.png",
    description:
      "B2B Growth Engineers. Safe, automated sales engine delivering predictable B2B meetings without hiring SDRs.",
    dateModified: SITE_LAST_UPDATED,
    contactPoint: {
      "@type": "ContactPoint",
      email: "main@agapeco.net",
      telephone: "+48-501-988-930",
      contactType: "customer support",
    },
    sameAs: ["https://www.instagram.com/agape.pl/", "https://www.linkedin.com/company/agapeconet/"],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://agapeco.net/#website",
    name: "AGAPE Automation Systems",
    url: "https://agapeco.net/",
    inLanguage: ["en", "pl", "be"],
    dateModified: SITE_LAST_UPDATED,
    publisher: { "@id": "https://agapeco.net/#organization" },
  },
]);

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ matches }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "robots", content: matches.length < 2 || matches.some((match) => match.status === "notFound") ? "noindex, nofollow" : "index, follow" },
      { name: "google-site-verification", content: "l3siqaIRAKdQxP6m0zxIOoDls-dGjabIl7gDYQEs2CQ" },
      { title: "AGAPE Automation Systems — B2B Growth Engineers" },
      {
        name: "description",
        content:
          "AGAPE Automation Systems builds the safe, automated sales engine that delivers predictable B2B meetings — without hiring SDRs.",
      },
      { name: "author", content: "AGAPE Automation Systems Sp. z o.o." },
      {
        name: "keywords",
        content:
          "B2B outreach, sales automation, AI engine, LinkedIn outreach, AEO, GEO, deliverability, AGAPE Octopus, AGAPE Researcher, AGAPE Liberty",
      },
      { name: "theme-color", content: "#121212" },
      { property: "og:type", content: "website" },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/5a5d6b00-619f-448d-8621-4ebccc2cfcb5",
      },
      { property: "og:site_name", content: "AGAPE Automation Systems" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@agape_consulting" },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/5a5d6b00-619f-448d-8621-4ebccc2cfcb5",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/agape-logo.png" },
      // Inter is self-hosted (see @font-face in src/styles.css): no third-party
      // stylesheet in the critical path. Only the latin subset is preloaded —
      // latin-ext (84 KB) is fetched on demand by the browser when a page
      // actually renders those glyphs, so it no longer competes with LCP.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        fetchPriority: "high",
        href: "/fonts/inter-latin.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      { type: "application/ld+json", children: ORGANIZATION_JSONLD },
      { children: LINKEDIN_LOADER },
      { children: COOKIE_PREHYDRATION_SCRIPT },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    // Forced dark theme site-wide per AGAPE brand (analytical brutalism) —
    // ported from main.tsx; rendered server-side so there is no theme flash.
    <html lang="en" className="dark" style={{ colorScheme: "dark" }} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // ported from main.tsx — real-user Core Web Vitals monitoring
  useEffect(() => {
    initWebVitals();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Sonner />
        </Suspense>
        <ScrollToTop />
        <LanguageProvider>
          <CookieConsentProvider>
            <ConsentScripts />
            <CookieBanner />
            <PersonaFiPopup />

            <Outlet />
          </CookieConsentProvider>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-md border border-border text-foreground font-medium"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
